/**
 * Combat sound recipes (pure data). Every sound is synthesized at play time from a few layers:
 *  - `noise`: a pre-rendered white-noise buffer through a biquad filter whose cutoff can sweep
 *    (whooshes, crunches, crackle, rumble);
 *  - `tone`: an oscillator with an optional pitch sweep and optional FM (thumps, metal partials,
 *    bells, chimes).
 * Each layer has a fast attack and an exponential decay. Frequencies are jittered per play
 * (engine.ts) so repeated hits never sound machine-gunned. No samples, nothing to license.
 *
 * Design rules: impacts are short and low-mid heavy (they should feel weighty, not shrill);
 * healing is soft and tonal; danger (enemy slams, the player getting hit) is dull and low so it
 * reads as "bad" without being louder than the player's own hits.
 */

export interface NoiseLayer {
  k: 'noise';
  /** Start offset (s). */
  t?: number;
  /** Length (s) and attack (s). */
  d: number;
  a?: number;
  /** Peak gain. */
  g: number;
  filter: BiquadFilterType;
  /** Cutoff / centre (Hz) at the start, optional sweep target. */
  f: number;
  f1?: number;
  q?: number;
}

export interface ToneLayer {
  k: 'tone';
  t?: number;
  d: number;
  a?: number;
  g: number;
  w?: OscillatorType;
  /** Frequency (Hz) at the start, optional sweep target. */
  f: number;
  f1?: number;
  /** FM modulator ratio (× f) and depth (Hz). Metal / bells. */
  fm?: number;
  fmDepth?: number;
}

export type Layer = NoiseLayer | ToneLayer;

export interface Recipe {
  layers: readonly Layer[];
  /** Voice priority (higher survives the limiter). */
  prio: number;
  /** Recipe gain (applied on top of the per-play gain). */
  gain?: number;
}

const n = (filter: BiquadFilterType, f: number, d: number, g: number, o: Partial<NoiseLayer> = {}): NoiseLayer => ({ k: 'noise', filter, f, d, g, ...o });
const tone = (f: number, d: number, g: number, o: Partial<ToneLayer> = {}): ToneLayer => ({ k: 'tone', f, d, g, ...o });

/** Low sine "thump" with a downward sweep: the body of every heavy hit. */
const thump = (f: number, d: number, g: number, t = 0): ToneLayer => tone(f, d, g, { f1: f * 0.42, w: 'sine', t });
/** Short broadband transient (the "contact"). */
const click = (g: number, f = 3200, t = 0): NoiseLayer => n('highpass', f, 0.025, g, { a: 0.001, t });
/** Inharmonic metal partial (FM sine). */
const metal = (f: number, d: number, g: number, t = 0): ToneLayer => tone(f, d, g, { fm: 1.414, fmDepth: f * 0.35, t, a: 0.001 });

export const RECIPES: Readonly<Record<string, Recipe>> = {
  // --- melee --------------------------------------------------------------
  swing: { prio: 2, gain: 0.55, layers: [n('bandpass', 500, 0.17, 0.7, { f1: 2200, q: 1.4, a: 0.04 })] },
  'swing.heavy': { prio: 2, gain: 0.6, layers: [n('bandpass', 320, 0.26, 0.8, { f1: 1500, q: 1.2, a: 0.07 }), n('lowpass', 260, 0.22, 0.35, { a: 0.06 })] },
  'hit.blade': {
    prio: 4,
    layers: [click(0.5), n('bandpass', 2600, 0.07, 0.6, { q: 1.2 }), metal(1830, 0.11, 0.14), thump(150, 0.08, 0.55)],
  },
  'hit.blade.heavy': {
    prio: 5,
    layers: [click(0.6, 2600), n('bandpass', 1500, 0.12, 0.7, { f1: 700, q: 0.9 }), metal(1240, 0.18, 0.16), thump(110, 0.18, 0.9), n('lowpass', 700, 0.16, 0.4)],
  },
  'hit.blunt': {
    prio: 5,
    layers: [click(0.45, 1800), thump(95, 0.2, 1.0), n('lowpass', 900, 0.16, 0.7, { f1: 300 }), n('bandpass', 400, 0.1, 0.35, { q: 2 })],
  },
  'hit.shield': {
    prio: 5,
    // A shield clang: metal partials ring over a heavy thump.
    layers: [click(0.5, 2200), thump(105, 0.2, 0.9), metal(520, 0.34, 0.22), metal(1310, 0.26, 0.14), metal(2150, 0.16, 0.08), n('lowpass', 1100, 0.12, 0.45)],
  },
  'hit.earth': {
    prio: 5,
    layers: [click(0.4, 1500), thump(70, 0.3, 1.0), n('lowpass', 420, 0.45, 0.75, { f1: 120 }), n('highpass', 1800, 0.18, 0.25, { t: 0.04, a: 0.01 })],
  },
  'hit.execute': {
    prio: 6,
    layers: [click(0.7, 2000), thump(85, 0.3, 1.1), n('lowpass', 1300, 0.22, 0.75, { f1: 260 }), metal(980, 0.28, 0.15), n('lowpass', 260, 0.5, 0.45, { t: 0.05, f1: 90 })],
  },
  // --- spells -------------------------------------------------------------
  'hit.lightning': {
    prio: 4,
    // Snap + crackle + a short electric buzz.
    layers: [click(0.7, 4200), n('highpass', 3000, 0.16, 0.5, { f1: 5200, a: 0.002 }), tone(95, 0.13, 0.22, { w: 'sawtooth', f1: 70 }), n('bandpass', 1200, 0.09, 0.35, { q: 3 })],
  },
  'hit.lightning.big': {
    prio: 6,
    // Thunder: a crack then a low rolling body.
    layers: [click(0.9, 3800), n('highpass', 2500, 0.2, 0.55, { f1: 5000 }), n('lowpass', 520, 0.9, 0.85, { f1: 110, a: 0.02, t: 0.02 }), thump(70, 0.35, 0.8), tone(85, 0.2, 0.18, { w: 'sawtooth', f1: 55 })],
  },
  chain: { prio: 3, gain: 0.7, layers: [click(0.5, 4500), n('highpass', 3500, 0.1, 0.45, { f1: 6000 }), tone(120, 0.08, 0.15, { w: 'sawtooth', f1: 90 })] },
  'hit.fire': {
    prio: 5,
    // Lava burst: a deep whump with crackle.
    layers: [thump(90, 0.26, 0.95), n('lowpass', 1000, 0.36, 0.85, { f1: 180, a: 0.008 }), n('highpass', 2400, 0.22, 0.2, { t: 0.05, a: 0.02 }), n('bandpass', 600, 0.14, 0.35, { q: 1.5 })],
  },
  'hit.fire.small': { prio: 3, gain: 0.7, layers: [n('lowpass', 1400, 0.18, 0.6, { f1: 400, a: 0.01 }), n('highpass', 2600, 0.12, 0.18, { t: 0.03 })] },
  'hit.holy': {
    prio: 4,
    // Bright but soft: a bell fifth over a gentle thump.
    layers: [thump(140, 0.12, 0.5), tone(880, 0.42, 0.16, { a: 0.004 }), tone(1320, 0.36, 0.11, { a: 0.004 }), tone(1760, 0.28, 0.07, { fm: 2, fmDepth: 40 }), n('highpass', 4000, 0.12, 0.18)],
  },
  'hit.holy.big': {
    prio: 5,
    layers: [thump(110, 0.22, 0.75), tone(660, 0.7, 0.16, { a: 0.006 }), tone(990, 0.6, 0.13), tone(1320, 0.5, 0.1), tone(1980, 0.4, 0.06, { fm: 2, fmDepth: 60 }), n('lowpass', 1600, 0.3, 0.4, { f1: 300 })],
  },
  'hit.shadow': { prio: 4, layers: [thump(90, 0.16, 0.6), n('bandpass', 700, 0.24, 0.5, { f1: 300, q: 2.5 }), tone(220, 0.22, 0.12, { w: 'triangle', f1: 140, fm: 0.5, fmDepth: 50 })] },
  'hit.arcane': { prio: 4, layers: [thump(130, 0.1, 0.45), tone(740, 0.25, 0.14, { fm: 3, fmDepth: 220, f1: 990 }), n('highpass', 3500, 0.1, 0.2)] },
  'hit.venom': { prio: 3, layers: [n('bandpass', 900, 0.18, 0.55, { f1: 400, q: 3 }), thump(120, 0.1, 0.4), n('highpass', 2000, 0.12, 0.15, { t: 0.04 })] },
  'hit.frost': { prio: 4, layers: [click(0.5, 5000), metal(2800, 0.16, 0.12), metal(3900, 0.12, 0.08), thump(140, 0.1, 0.45), n('highpass', 4200, 0.14, 0.25)] },
  // --- support ------------------------------------------------------------
  heal: { prio: 2, gain: 0.8, layers: [tone(660, 0.36, 0.14, { f1: 880, a: 0.02 }), tone(990, 0.4, 0.09, { a: 0.03, t: 0.04 }), n('highpass', 5000, 0.2, 0.06, { a: 0.03 })] },
  'heal.big': {
    prio: 3,
    layers: [tone(523, 0.7, 0.13, { a: 0.03 }), tone(659, 0.7, 0.11, { a: 0.03, t: 0.05 }), tone(784, 0.7, 0.1, { a: 0.03, t: 0.1 }), tone(1047, 0.6, 0.08, { a: 0.04, t: 0.15 }), n('highpass', 5200, 0.4, 0.07, { a: 0.08 })],
  },
  nova: { prio: 3, gain: 0.75, layers: [n('bandpass', 300, 0.4, 0.6, { f1: 1600, q: 1.0, a: 0.05 }), tone(330, 0.5, 0.1, { f1: 660, a: 0.06 })] },
  'cast.fire': { prio: 2, gain: 0.6, layers: [n('lowpass', 500, 0.22, 0.6, { f1: 1800, a: 0.03 })] },
  'cast.holy': { prio: 2, gain: 0.55, layers: [tone(880, 0.25, 0.1, { f1: 1320, a: 0.03 }), n('highpass', 4500, 0.15, 0.08, { a: 0.03 })] },
  'shield.up': { prio: 3, gain: 0.7, layers: [tone(392, 0.6, 0.12, { a: 0.04 }), tone(587, 0.55, 0.1, { a: 0.05, t: 0.03 }), n('bandpass', 900, 0.3, 0.25, { f1: 2400, q: 1.5, a: 0.05 })] },
  // --- moments ------------------------------------------------------------
  /** Layered on top of a crit's impact: a short bright "shing". Moderate: it reads, it doesn't pierce. */
  crit: { prio: 6, gain: 0.75, layers: [n('highpass', 5200, 0.09, 0.35, { a: 0.002 }), tone(2300, 0.14, 0.09, { f1: 3100, fm: 1.5, fmDepth: 300 }), tone(1150, 0.18, 0.07)] },
  /** An enemy dies to the player: a satisfying pop. */
  kill: { prio: 6, layers: [thump(220, 0.14, 0.8), n('bandpass', 900, 0.1, 0.5, { q: 1.4 }), tone(1200, 0.16, 0.08, { f1: 1800, t: 0.03 }), n('lowpass', 500, 0.3, 0.35, { t: 0.04, f1: 120 })] },
  interrupt: {
    prio: 7,
    // Clank + a fizzle down: the enemy's spell breaks.
    layers: [click(0.7, 2000), metal(700, 0.22, 0.2), metal(1900, 0.16, 0.12), thump(160, 0.1, 0.6), tone(900, 0.2, 0.09, { f1: 260, w: 'triangle', t: 0.04 })],
  },
  /** A proc lit up: a clean two-note ding. */
  proc: { prio: 6, gain: 0.8, layers: [tone(1319, 0.5, 0.13, { a: 0.003, fm: 2, fmDepth: 30 }), tone(1976, 0.55, 0.1, { a: 0.003, t: 0.07, fm: 2, fmDepth: 30 })] },
  dodge: { prio: 5, gain: 0.85, layers: [n('bandpass', 380, 0.28, 0.75, { f1: 2400, q: 1.6, a: 0.02 }), n('highpass', 3000, 0.12, 0.18, { t: 0.06, a: 0.03 })] },
  // --- movement -----------------------------------------------------------
  /** A soft footstep on stone / packed dirt (render/toon/feel/footsteps.ts): a dull tap and a short grit. Lowest priority. */
  step: { prio: 0, gain: 0.5, layers: [thump(120, 0.05, 0.4), n('lowpass', 1100, 0.07, 0.55, { a: 0.002, f1: 380 }), n('bandpass', 2600, 0.035, 0.14, { q: 1.4, a: 0.001, t: 0.004 })] },
  // --- danger -------------------------------------------------------------
  /** The player took a big hit: a dull body blow. */
  hurt: { prio: 6, layers: [thump(80, 0.22, 0.9), n('lowpass', 500, 0.2, 0.6, { f1: 160 }), n('bandpass', 300, 0.12, 0.3, { q: 2 })] },
  /** Enemy melee landing on a party member (quiet, low). */
  'enemy.hit': { prio: 1, gain: 0.55, layers: [thump(120, 0.1, 0.6), n('lowpass', 800, 0.09, 0.4)] },
  /** An enemy telegraph resolved (danger): a low boom. */
  slam: { prio: 6, gain: 0.85, layers: [thump(60, 0.4, 1.0), n('lowpass', 360, 0.55, 0.7, { f1: 90, a: 0.01 }), click(0.3, 1200)] },
  /** A boss / dangerous telegraph appeared: a low swell with a dull bell on top (cuts through, never shrill). */
  telegraph: {
    prio: 7,
    gain: 0.8,
    layers: [tone(98, 0.55, 0.7, { a: 0.12, f1: 82 }), n('bandpass', 280, 0.5, 0.45, { f1: 900, q: 1.6, a: 0.18 }), tone(587, 0.5, 0.08, { a: 0.01, fm: 1.414, fmDepth: 120 }), tone(880, 0.35, 0.05, { a: 0.01, t: 0.02 })],
  },
  /** Your cast broke (moved, stunned): a short downward fizzle. */
  'cast.fail': { prio: 3, gain: 0.6, layers: [n('highpass', 2600, 0.2, 0.4, { f1: 700, a: 0.004 }), tone(620, 0.16, 0.12, { w: 'triangle', f1: 210, a: 0.004 })] },
  /** Low-health heartbeat fallback: a soft lub-dub. */
  heartbeat: { prio: 5, gain: 0.8, layers: [thump(62, 0.13, 0.9), thump(56, 0.15, 0.7, 0.21)] },
  // --- +field: progression moments ---------------------------------------------
  /**
   * Level-up burst (renderer, at the pillar's burst): an air whoosh rising into a deep boom, a
   * bright major-chord shimmer and a long airy tail up the column. Epic, but low-mid heavy so it
   * sits under the HUD's `level.up` stinger instead of fighting it.
   */
  'level.impact': {
    prio: 8,
    gain: 0.95,
    layers: [
      n('bandpass', 260, 0.32, 0.55, { f1: 2600, q: 1.1, a: 0.22 }),
      thump(68, 0.6, 1.1, 0.0),
      thump(44, 0.9, 0.7, 0.02),
      click(0.35, 1600),
      n('lowpass', 900, 0.7, 0.55, { f1: 140, a: 0.01 }),
      tone(523, 1.3, 0.1, { a: 0.02, t: 0.02 }),
      tone(659, 1.25, 0.08, { a: 0.03, t: 0.05 }),
      tone(784, 1.2, 0.07, { a: 0.03, t: 0.08 }),
      tone(1047, 1.1, 0.06, { a: 0.04, t: 0.11, fm: 2, fmDepth: 25 }),
      tone(1568, 0.9, 0.035, { a: 0.05, t: 0.14, fm: 3, fmDepth: 40 }),
      n('highpass', 4200, 1.4, 0.12, { a: 0.25, t: 0.05, f1: 7000 }),
    ],
  },
  /** A bounty turned in: a quick rising arpeggio over a soft coin shimmer. */
  'bounty.complete': {
    prio: 6,
    gain: 0.85,
    layers: [
      tone(784, 0.3, 0.12, { a: 0.005 }),
      tone(988, 0.3, 0.11, { a: 0.005, t: 0.07 }),
      tone(1175, 0.34, 0.1, { a: 0.005, t: 0.14 }),
      tone(1568, 0.6, 0.1, { a: 0.006, t: 0.21, fm: 2, fmDepth: 20 }),
      metal(2600, 0.25, 0.04, 0.18),
      metal(3300, 0.2, 0.03, 0.26),
      n('highpass', 5500, 0.35, 0.06, { a: 0.02, t: 0.2 }),
    ],
  },
  // --- +jump ---------------------------------------------------------------
  /** Push-off: a short cloth whoosh and a light scuff. */
  'move.jump': { prio: 2, gain: 0.5, layers: [n('bandpass', 700, 0.16, 0.35, { f1: 1800, q: 0.9, a: 0.02 }), n('lowpass', 900, 0.06, 0.25, { a: 0.002 })] },
  /** Landing: a soft dull thud with a grit of gravel. */
  'move.land': { prio: 2, gain: 0.6, layers: [thump(90, 0.14, 0.7), n('lowpass', 1300, 0.12, 0.35, { f1: 400, a: 0.002 }), n('highpass', 2800, 0.05, 0.08, { a: 0.002, t: 0.01 })] },
  // --- +field: reward pops landing in the HUD (small, satisfying, rate-limited by the HUD) ---
  /** XP pop lands in the bar: a soft bright two-note chime. */
  'reward.xp': { prio: 2, gain: 0.45, layers: [tone(1318, 0.18, 0.09, { a: 0.004 }), tone(1976, 0.22, 0.06, { a: 0.004, t: 0.05, fm: 2, fmDepth: 12 }), n('highpass', 6000, 0.12, 0.03, { a: 0.01 })] },
  /** Gold pop lands: a coin clink. */
  'reward.coin': { prio: 2, gain: 0.5, layers: [metal(3100, 0.14, 0.07), metal(4200, 0.1, 0.05, 0.035), click(0.12, 5200)] },
  /** A gear drop lands: a glinting sparkle. */
  'reward.item': { prio: 3, gain: 0.55, layers: [tone(1568, 0.25, 0.07, { a: 0.004 }), tone(2093, 0.3, 0.06, { a: 0.004, t: 0.06 }), metal(3600, 0.2, 0.04, 0.08)] },
  /** A rare-or-better drop lands: a brighter rising arpeggio with a shimmer tail. */
  'reward.rare': { prio: 5, gain: 0.7, layers: [tone(988, 0.3, 0.09, { a: 0.004 }), tone(1319, 0.3, 0.08, { a: 0.004, t: 0.06 }), tone(1976, 0.5, 0.08, { a: 0.006, t: 0.12, fm: 2, fmDepth: 20 }), n('highpass', 5200, 0.5, 0.06, { a: 0.03, t: 0.1 })] },
  // --- ui -----------------------------------------------------------------
  /** +castux: UI error ("Out of range", "Not ready yet"): a soft, low two-note "nope" (hud/uiErrors.ts, rate-limited). */
  'ui.error': { prio: 3, gain: 0.5, layers: [tone(330, 0.09, 0.2, { w: 'triangle', a: 0.006 }), tone(247, 0.13, 0.2, { w: 'triangle', a: 0.006, t: 0.085 }), n('lowpass', 900, 0.06, 0.08, { a: 0.004 })] },
};

/** Total length of a recipe in seconds. */
export function recipeDuration(r: Recipe): number {
  let d = 0;
  for (const l of r.layers) d = Math.max(d, (l.t ?? 0) + l.d);
  return d;
}
